import React from 'react';

import { cn } from '@change/ds-utils';

import { useSignerCarouselColorsExperiment } from '#src/app/pages/petitionDetails/shared/hooks';

import { ColoredRecentSignersFeedItem } from './components/ColoredRecentSignersFeedItem';
import { RecentSignersFeedImpression } from './components/RecentSignersFeedImpression';
import { RecentSignersFeedItem } from './components/RecentSignersFeedItem';
import { useRecentSignersConnection } from './hooks/useRecentSignersConnection';
import { useScrollableFeed } from './hooks/useScrollableFeed';
import { MIN_SIGNERS_TO_SHOW } from './shared/constants';

const DEFAULT_INTERVAL_SECONDS = 3;

type Props = Readonly<{
	/** Merged onto the wrapper, e.g. to replace its bottom margin. */
	className?: string;
	/** The right-edge fade's end colour; `transparent` drops the overlay for non-white surfaces. */
	fadeTo?: 'inverse-strong' | 'transparent';
}>;

export const RecentSignersFeed: React.FC<Props> = ({ className, fadeTo = 'inverse-strong' }) => {
	const baseRecentSigners = useRecentSignersConnection();
	const scrollerRef = useScrollableFeed(DEFAULT_INTERVAL_SECONDS, baseRecentSigners.length);
	// EXPERIMENT: sign_form_signer_carousel_colors_experiment — remove with experiment cleanup
	const { colorScheme } = useSignerCarouselColorsExperiment();

	if (baseRecentSigners.length < MIN_SIGNERS_TO_SHOW) {
		return null;
	}

	// Doubling the recent signers to create a seamless scrolling effect.
	const recentSigners = [...baseRecentSigners, ...baseRecentSigners];

	return (
		<div className={cn('relative mb-7', className)}>
			{/* EXPERIMENT: sign_form_signer_carousel_colors_experiment — remove with experiment cleanup */}
			{/* Absolute, so Waypoint's in-flow span adds no line box; pinned to the row's top so it enters with it. */}
			<div aria-hidden className="pointer-events-none absolute inset-x-0 top-0">
				<RecentSignersFeedImpression signerCount={baseRecentSigners.length} />
			</div>
			<div
				ref={scrollerRef}
				className="flex snap-x snap-proximity flex-row gap-3 overflow-x-hidden scroll-smooth whitespace-nowrap"
			>
				{recentSigners.map((signer, index) =>
					// EXPERIMENT: sign_form_signer_carousel_colors_experiment — remove with experiment cleanup
					colorScheme === 'default' ? (
						<RecentSignersFeedItem key={`${signer.id}-${index}`} item={signer} />
					) : (
						<ColoredRecentSignersFeedItem
							key={`${signer.id}-${index}`}
							item={signer}
							colorScheme={colorScheme}
							baseIndex={index % baseRecentSigners.length}
						/>
					),
				)}
			</div>
			{fadeTo === 'inverse-strong' && (
				<div
					aria-hidden
					className="pointer-events-none absolute top-0 right-0 h-full w-full bg-[linear-gradient(to_right,transparent_80%,var(--color-fill-inverse-strong))]"
				/>
			)}
		</div>
	);
};
